// ============================================================
// Even Hub SDK bridge wrapper
// ============================================================
// 例外時のリトライ、ライフサイクルイベントの集約を担う。

import {
  waitForEvenAppBridge,
  OsEventTypeList,
  type EvenAppBridge,
} from '@evenrealities/even_hub_sdk'

export type AppEventType =
  | 'click'
  | 'doubleClick'
  | 'scrollTop'
  | 'scrollBottom'
  | 'foregroundEnter'
  | 'foregroundExit'
  | 'abnormalExit'

export interface AppEvent {
  type: AppEventType
}

export type AppEventHandler = (e: AppEvent) => void

export class Bridge {
  private bridge: EvenAppBridge | null = null
  private handlers = new Set<AppEventHandler>()
  private rawHandlers = new Set<(e: any) => void>()
  private unsubscribe: (() => void) | null = null

  async connect(): Promise<EvenAppBridge> {
    if (this.bridge) return this.bridge
    this.bridge = await waitForEvenAppBridge()

    this.unsubscribe = this.bridge.onEvenHubEvent((event: any) => {
      // raw event を全 listener に流す (debug 用)
      this.rawHandlers.forEach((h) => {
        try { h(event) } catch (err) { console.warn('[Bridge] raw handler error', err) }
      })
      // textEvent / sysEvent / listEvent のどれにあっても拾う
      let t: number | undefined =
        event?.textEvent?.eventType ??
        event?.sysEvent?.eventType ??
        event?.listEvent?.eventType ??
        event?.eventType

      // 🩹 Workaround: simulator (および一部の host) は単発タップを
      //    {sysEvent: {eventSource: 1, eventType: undefined}} の形で送ってくる。
      //    eventType が空でも touch source (>0) が立っていれば CLICK_EVENT とみなす。
      if (t == null) {
        const src = event?.sysEvent?.eventSource
        if (typeof src === 'number' && src > 0) {
          t = OsEventTypeList.CLICK_EVENT
        }
      }

      if (t == null) return
      const ev = mapEvent(t)
      if (ev) this.dispatch(ev)
    })

    return this.bridge
  }

  /** raw event 用の subscriber (デバッグ・未知イベントの可視化) */
  onRawEvent(handler: (event: any) => void): () => void {
    this.rawHandlers.add(handler)
    return () => this.rawHandlers.delete(handler)
  }

  raw(): EvenAppBridge {
    if (!this.bridge) throw new Error('Bridge not connected yet')
    return this.bridge
  }

  on(handler: AppEventHandler): () => void {
    this.handlers.add(handler)
    return () => this.handlers.delete(handler)
  }

  disconnect(): void {
    this.unsubscribe?.()
    this.unsubscribe = null
  }

  // ───────── private ─────────

  private dispatch(ev: AppEvent): void {
    this.handlers.forEach((h) => h(ev))
  }
}

function mapEvent(t: number): AppEvent | null {
  switch (t) {
    case OsEventTypeList.CLICK_EVENT:
      return { type: 'click' }
    case OsEventTypeList.DOUBLE_CLICK_EVENT:
      return { type: 'doubleClick' }
    case OsEventTypeList.SCROLL_TOP_EVENT:
      return { type: 'scrollTop' }
    case OsEventTypeList.SCROLL_BOTTOM_EVENT:
      return { type: 'scrollBottom' }
    case OsEventTypeList.FOREGROUND_ENTER_EVENT:
      return { type: 'foregroundEnter' }
    case OsEventTypeList.FOREGROUND_EXIT_EVENT:
      return { type: 'foregroundExit' }
    case OsEventTypeList.ABNORMAL_EXIT_EVENT:
      return { type: 'abnormalExit' }
    default:
      return null
  }
}
